// 카피바라 자세: 숨쉬기, 감정 동작, 씹기, 걷기·헤엄, 씻기, 찜질방 동작, 물에 잠기기, 눕기, 물결, 코 풍선, 소품 튕기기.
// 프로토타입 pose 1938–2113. 음식·올라타기·소품 효과는 해당 모듈이 옮겨질 때 poseHooks로 끼운다.
import * as THREE from 'three';
import { angDiff, easeOutBounce } from '../engine/math';
import type { Capy, EmoteKey } from './agent';

export type Room = 'quiet' | 'lagoon' | 'jjim';

export interface PoseContext {
  room: Room;
}

/** 감정 동작별 길이 (초). */
export const EMOTES: Record<EmoteKey, { dur: number }> = {
  wave: { dur: 2.8 },
  wobble: { dur: 2.6 },
  waddle: { dur: 3.2 },
  yawn: { dur: 3.4 },
};

/** 온천·라군 수면 높이, 찜질방 탕 수면 높이. */
export const WATER_Y = -0.14, JW_Y = 0.35;

/** 한 프레임에 쌓아 가는 관절 값. 훅도 이 값을 고친다. */
export interface PoseVals {
  hx: number; hz: number; rz: number; by: number; mouthOpen: number;
  armR: number; armRz: number; armL: number; armLz: number;
  footL: number; footR: number; footLz: number; footRz: number;
}

export interface PoseHooks {
  /** 먹는 중 팔·입 동작. a.fx를 채운다 (food/eating). */
  eating?(a: Capy, t: number, v: PoseVals): void;
  /** 손에 든 음료 마시기 (food/eating). */
  carry?(a: Capy, t: number, dt: number, v: PoseVals, moving: boolean, ctx: PoseContext): void;
  /** 씻기가 끝났을 때. 팩을 직접 처리했으면 true (라군 팩 지우기). */
  washEnd?(a: Capy, finished: boolean): boolean;
  /** 다른 카피바라 머리 위 자리 맞추기 (stacking). */
  rider?(a: Capy, t: number): void;
  /** 손에 든 음식·컵 위치 맞추기 (food/eating). */
  held?(a: Capy, t: number): void;
  /** 머리 소품 효과 (items/fx). */
  itemFx?(a: Capy, t: number, dt: number, ctx: PoseContext): void;
}
export const poseHooks: PoseHooks = {};

const LQ = { y: new THREE.Quaternion(), r: new THREE.Quaternion(), l: new THREE.Quaternion(), ax: new THREE.Vector3(1, 0, 0), ay: new THREE.Vector3(0, 1, 0), az: new THREE.Vector3(0, 0, 1) };
const v: PoseVals = { hx: 0, hz: 0, rz: 0, by: 0, mouthOpen: 0, armR: 0, armRz: 0, armL: 0, armLz: 0, footL: 0, footR: 0, footLz: 0, footRz: 0 };

const sm = (x: number) => { const k = Math.max(0, Math.min(1, x)); return k * k * (3 - 2 * k); };
/** 바가지 한 번 (q 0~1): 대야에서 물 뜨기(dip) → 팔을 바깥쪽 위로 사선으로 들기(up) → 머리 쪽으로 기울여 끼얹기(pour) → 내리기. 찜질방 씻는 자리 (rooms/jjim/dipper.ts와 같이 쓴다). */
export function washCycle(q: number): { dip: number; up: number; pour: number } {
  const dip = q < 0.2 ? Math.sin(q / 0.2 * Math.PI) : 0;
  const up = q < 0.2 ? 0 : q < 0.45 ? sm((q - 0.2) / 0.25) : q < 0.78 ? 1 : 1 - sm((q - 0.78) / 0.22);
  const pour = q < 0.5 ? 0 : q < 0.58 ? sm((q - 0.5) / 0.08) : q < 0.68 ? 1 : 1 - sm((q - 0.68) / 0.08);
  return { dip, up, pour };
}

export function startEmote(a: Capy, key: EmoteKey, t: number): void {
  a.napping = false;
  a.emote = key;
  a.emoteStart = t;
}

export function pose(a: Capy, t: number, dt: number, moving: boolean, ctx: PoseContext): void {
  const c = a.c, sp = a.seedP, room = ctx.room;
  a.napAmt += ((a.napping ? 1 : 0) - a.napAmt) * Math.min(1, dt * 2.5);
  a.breath += dt * (1.6 - 0.8 * a.napAmt);
  v.hx = 0.025 * Math.sin(t * 0.6 + sp) + 0.28 * a.napAmt;
  v.hz = 0.03 * Math.sin(t * 0.45 + sp) * (1 - a.napAmt) + 0.08 * a.napAmt;
  v.rz = 0.015 * Math.sin(t * 0.8 + sp); v.by = 0; v.mouthOpen = 0;
  v.armR = 0; v.armRz = 0; v.armL = 0; v.armLz = 0; v.footL = 0; v.footR = 0; v.footLz = 0; v.footRz = 0;

  if (a.emote) {
    const p = (t - a.emoteStart) / EMOTES[a.emote].dur;
    if (p >= 1) a.emote = null;
    else {
      const env = Math.sin(p * Math.PI);
      if (a.emote === 'wave') {
        const up = Math.min(1, Math.sin(Math.min(p, 0.999) * Math.PI) * 1.6);
        v.armR = -1.8 * up; v.armRz = 0.4 * Math.sin(p * Math.PI * 6) * up; v.hz += 0.08 * up;
      } else if (a.emote === 'wobble') {
        v.rz += 0.13 * Math.sin(p * Math.PI * 4) * env; v.hz -= 0.1 * Math.sin(p * Math.PI * 4) * env;
      } else if (a.emote === 'waddle') {
        const w = Math.sin(p * Math.PI * 6);
        v.rz += 0.1 * w * env; v.by = 0.07 * Math.abs(w) * env;
        v.footL = Math.max(0, w) * 0.12 * env; v.footR = Math.max(0, -w) * 0.12 * env;
        v.armL = -0.35 * w * env; v.armR = 0.35 * w * env;
      } else if (a.emote === 'yawn') {
        v.hx -= 0.32 * env; v.mouthOpen = env; v.armL = -0.5 * env; v.armR = -0.5 * env;
      }
    }
  }

  a.fx = null;
  poseHooks.eating?.(a, t, v);

  // 씹기: 입과 고개를 빠르게 조금씩, 볼은 부풀게
  a.chew = (a.chew || 0) + (((a.chewUntil || 0) > t ? 1 : 0) - (a.chew || 0)) * Math.min(1, dt * 8);
  if (a.chew > 0.01) {
    const w = Math.sin(t * 15);
    v.mouthOpen = Math.max(v.mouthOpen, a.chew * (0.1 + 0.18 * (0.5 + 0.5 * w)));
    v.hx += 0.03 * w * a.chew;
  }
  c.headG.scale.set(1 + 0.035 * a.chew * (0.5 + 0.5 * Math.sin(t * 15 + 1.2)), 1, 1 + 0.02 * a.chew);

  a.walkAmt += ((moving ? 1 : 0) - a.walkAmt) * Math.min(1, dt * 2.5);
  const swimW = room === 'lagoon' ? Math.max(0, (a.bathAmt - 0.4) / 0.6) : 0;
  a.swimK = a.walkAmt * swimW;
  if (a.walkAmt > 0.001) {
    a.walkPhase += dt * 2 * Math.PI * 0.85 * a.walkAmt * (1 + 0.7 * swimW);
    const w = Math.sin(a.walkPhase), k = a.walkAmt * (1 - swimW), sk = a.swimK;
    v.rz += 0.07 * w * k; v.by += 0.035 * Math.abs(w) * k;
    v.footL += Math.max(0, w) * 0.11 * k; v.footR += Math.max(0, -w) * 0.11 * k;
    v.footLz = 0.09 * w * k; v.footRz = -0.09 * w * k;
    v.armL += 0.3 * w * k; v.armR -= 0.3 * w * k;
    v.hz -= 0.05 * w * k; v.hx += 0.03 * Math.abs(w) * k;
    if (sk > 0.001) {
      v.armR += (-0.95 - 0.75 * w) * sk; v.armL += (-0.95 + 0.75 * w) * sk; v.armRz += 0.25 * sk; v.armLz -= 0.25 * sk;
      v.footLz += 0.25 * Math.sin(a.walkPhase * 2) * sk; v.footRz -= 0.25 * Math.sin(a.walkPhase * 2) * sk;
      v.hx -= 0.32 * sk; v.rz += 0.04 * w * sk;
    }
  }

  // 씻기: 두 팔로 얼굴 문지르기. 팩이 있으면 서서히 사라진다.
  if (a.washT) {
    const p = (t - a.washT) / 3.2;
    if (moving || p >= 1) {
      const handled = poseHooks.washEnd?.(a, p >= 1) ?? false;
      if (!handled && a.mask) a.mask.scale.setScalar(1);
      a.washT = 0;
    } else {
      const rub = Math.sin(t * 9), env = Math.min(1, p * 4, (1 - p) * 4);
      v.hx -= 0.38 * env; v.mouthOpen = Math.max(v.mouthOpen, 0.2 * env);
      v.armR = -1.55 * env + 0.22 * rub * env; v.armL = -1.55 * env - 0.22 * rub * env; v.armRz = 0.55 * env; v.armLz = -0.55 * env;
      if (a.mask) a.mask.scale.setScalar(Math.max(0.01, 1 - Math.max(0, p - 0.25) / 0.75));
    }
  }

  if (room === 'jjim') {
    const L = a.lieAmt || 0;
    if (L > 0.01) { v.armR = v.armR * (1 - L) - 0.45 * L; v.armL = v.armL * (1 - L) - 0.45 * L; v.armRz = v.armRz * (1 - L) + 0.5 * L; v.armLz = v.armLz * (1 - L) - 0.5 * L; v.hx = v.hx * (1 - L) - 0.2 * L; }
    if (a.tray) { v.armR = -1.05; v.armL = -1.05; v.armRz = 0.25; v.armLz = -0.25; }
    if (a.washT && a.spot && !a.spotPending) {
      const p = (t - a.washT) / 3.2;
      if (a.spot.pose === 'wash') { const w = washCycle((p * 2) % 1); v.armR = -0.5 + 0.35 * w.dip - 1.4 * w.up; v.armRz = 1.15 * w.up; v.armL = -0.4; v.armLz = 0; v.hx += 0.18 * w.up + 0.12 * w.pour; }
      else if (a.spot.pose === 'shower' && p < 0.18) { const k3 = Math.sin(p / 0.18 * Math.PI); v.armL = -1.6 * k3; v.armLz = -0.3 * k3; }
    }
    if (a.scrubT) {
      const p = (t - a.scrubT) / 3.5;
      if (p < 1) { const env = Math.min(1, p * 5, (1 - p) * 5), w = Math.sin(t * 12); v.armR = (-0.15 + 0.06 * Math.abs(w)) * env; v.armRz = (-0.35 + 0.42 * w) * env; v.armL = -0.25 * env; v.armLz = 0.15 * env; v.rz += 0.035 * w * env; v.hx += 0.34 * env; }
      else a.scrubT = 0;
    }
    if (a.scratchT) {
      const p = (t - a.scratchT) / 3.5;
      // 효자손으로 귀 뒤 긁기: 팔을 높이 들어 효자손이 머리 옆을 지나 귀 뒤를 쓱쓱 (프로토타입은 팔·효자손이 머리를 뚫어서 바꿈)
      if (p < 1) { const env = Math.min(1, p * 5, (1 - p) * 5), w = Math.sin(t * 9); v.armR = (-2.55 + 0.1 * w) * env; v.armRz = 1.2 * env; v.hz -= 0.08 * env; v.hx += 0.06 * env; v.mouthOpen = Math.max(v.mouthOpen, 0.15 * env); }
      else a.scratchT = 0;
    }
    if ((a.cold || 0) > 0.05) {
      const k2 = a.cold!;
      v.rz += Math.sin(t * 50 + sp * 7) * 0.035 * k2; v.hz += Math.sin(t * 43 + sp) * 0.025 * k2;
      if (!a.eating && !a.tray && L < 0.5) { v.armR = v.armR * (1 - k2) - 0.8 * k2; v.armL = v.armL * (1 - k2) - 0.8 * k2; v.armRz = v.armRz * (1 - k2) - 0.5 * k2; v.armLz = v.armLz * (1 - k2) + 0.5 * k2; }
    }
    if ((a.spicyUntil || 0) > t) v.mouthOpen = Math.max(v.mouthOpen, 0.35 + 0.25 * Math.abs(Math.sin(t * 9)));
  }

  poseHooks.carry?.(a, t, dt, v, moving, ctx);

  c.body.scale.y = 1 + 0.015 * Math.sin(a.breath);
  c.body.scale.x = 1 - 0.006 * Math.sin(a.breath);
  c.headPivot.rotation.x = v.hx; c.headPivot.rotation.z = v.hz;
  c.root.rotation.z = v.rz; c.bodyG.position.y = v.by; c.bodyG.rotation.x = 0.07 * a.walkAmt * (1 - a.swimK) + 0.42 * a.swimK;
  c.arms[0]!.rotation.x = v.armR; c.arms[0]!.rotation.z = v.armRz; c.arms[1]!.rotation.x = v.armL; c.arms[1]!.rotation.z = v.armLz;
  c.feet[0]!.position.y = 0.1 + v.footR; c.feet[1]!.position.y = 0.1 + v.footL;
  c.feet[0]!.position.z = 0.78 + v.footRz; c.feet[1]!.position.z = 0.78 + v.footLz;
  c.mouth.visible = v.mouthOpen > 0.03; c.mouth.scale.y = 0.02 + v.mouthOpen; c.mouth.scale.x = 1 + 0.3 * v.mouthOpen;

  // 물에 잠기기, 벤치에 앉기, 찜질방 자리 높이
  const sink = a.bathAmt * a.bathAmt * (3 - 2 * a.bathAmt);
  a.benchAmt = (a.benchAmt || 0) + (((a.mode === 'wait' && a.seated) ? 1 : 0) - (a.benchAmt || 0)) * Math.min(1, dt * 3);
  const lift = a.benchAmt * a.benchAmt * (3 - 2 * a.benchAmt) * 0.47;
  a.seatLiftV = (a.seatLiftV || 0) + (((room === 'jjim' && a.spot && !a.spotPending) ? (a.spot.seatY || 0) : 0) - (a.seatLiftV || 0)) * Math.min(1, dt * 4);
  c.root.position.set(a.pos.x,
    (-(room === 'jjim' ? 1.25 : 1.55) * sink + 0.025 * Math.sin(t * 1.1 + sp) * a.bathAmt) * (a.size || 1) + lift + a.seatLiftV + (room === 'jjim' ? (a.bounceY || 0) + (a.elevY || 0) : 0),
    a.pos.z);
  a.facing += angDiff(a.targetFacing, a.facing) * Math.min(1, dt * 2.5);
  c.root.rotation.y = a.facing;
  c.root.rotation.x = room === 'jjim' && a.flipK ? -a.flipK * Math.PI * 2 : 0;
  a.lieAmt = (a.lieAmt || 0) + (((a.lying && room === 'jjim') ? 1 : 0) - (a.lieAmt || 0)) * Math.min(1, dt * 3);
  if (a.lieAmt > 0.003) {
    const L = a.lieAmt * a.lieAmt * (3 - 2 * a.lieAmt);
    LQ.y.setFromAxisAngle(LQ.ay, a.facing); LQ.r.setFromAxisAngle(LQ.az, (a.roll || 0) * L); LQ.l.setFromAxisAngle(LQ.ax, -Math.PI / 2 * L);
    c.root.quaternion.copy(LQ.y).multiply(LQ.r).multiply(LQ.l);
    c.root.position.y += 0.78 * (a.size || 1) * L;
  }
  if (a.riding || a.hop) poseHooks.rider?.(a, t);
  if (a.item) a.item.visible = !a.rider;

  if (a.bathAmt > 0.5) {
    a.rippleT += dt;
    const q = (a.rippleT % 3.6) / 3.6, mat = a.ripple.material as THREE.MeshBasicMaterial;
    a.ripple.visible = true;
    a.ripple.position.set(a.pos.x, (room === 'jjim' ? JW_Y : WATER_Y) + 0.015, a.pos.z);
    a.ripple.scale.setScalar((0.75 + q * 0.8) * (a.size < 1 ? 0.6 : 1));
    mat.opacity = 0.28 * (1 - q) * (a.bathAmt - 0.5) * 2;
  } else a.ripple.visible = false;

  // 코 풍선: 숨에 맞춰 커졌다 작아지고, 낮잠 중에는 가끔 터진다
  if (a.bubble) {
    a.bubbleK = Math.min(1, (a.bubbleK == null ? 1 : a.bubbleK) + dt / 1.8);
    const br = 0.5 + 0.5 * Math.sin(a.breath), nap = a.napAmt;
    if (nap > 0.5 && br > 0.985 && a.bubbleK >= 1 && Math.random() < 0.06) a.bubbleK = 0;
    const r = (0.07 + 0.05 * br + nap * (0.08 + 0.09 * br)) * a.bubbleK;
    a.bubble.scale.setScalar(Math.max(0.001, r));
    a.bubble.visible = r > 0.004;
  }
  poseHooks.held?.(a, t);
  if (a.mask && a.maskPop) { const k = Math.min(1, (t - a.maskPop) / 0.6); a.mask.scale.setScalar(Math.max(0.01, k)); if (k >= 1) a.maskPop = 0; }
  poseHooks.itemFx?.(a, t, dt, ctx);
  if (a.item && a.dropStart >= 0) {
    const p = (t - a.dropStart) / 0.55;
    if (p >= 1) { a.item.position.y = 0; a.dropStart = -1; }
    else a.item.position.y = 0.55 * (1 - easeOutBounce(p));
  }
}
